/**
 * A tiny, pure `Result<T, E>` — a discriminated union that makes fallible
 * operations return their failure instead of throwing. Nothing here throws;
 * callers branch on `ok`. Use it for domain flows where an error is an expected
 * outcome (validation, quota checks) rather than an exceptional one.
 */

/** The success arm — carries the value under `value`. */
export interface Ok<T> {
  readonly ok: true;
  readonly value: T;
}

/** The failure arm — carries the error under `error`. */
export interface Err<E> {
  readonly ok: false;
  readonly error: E;
}

/** A value that is either an `Ok<T>` or an `Err<E>`. */
export type Result<T, E> = Ok<T> | Err<E>;

/** Wrap a success value. */
export function ok<T>(value: T): Ok<T> {
  return { ok: true, value };
}

/** Wrap a failure value. */
export function err<E>(error: E): Err<E> {
  return { ok: false, error };
}

/** Type guard narrowing a `Result` to its success arm. */
export function isOk<T, E>(result: Result<T, E>): result is Ok<T> {
  return result.ok;
}

/** Type guard narrowing a `Result` to its failure arm. */
export function isErr<T, E>(result: Result<T, E>): result is Err<E> {
  return !result.ok;
}
